import { ErrorHandler, Injectable } from '@angular/core';
import type { StackFrame } from './stack-parser';

function toError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'object' && value !== null && typeof (value as any).message === 'string') {
    const err = new Error((value as any).message);
    err.name = (value as any).name ?? 'Error';
    if (typeof (value as any).url === 'string') (err as any).url = (value as any).url;
    if (typeof (value as any).status === 'number') (err as any).status = (value as any).status;
    return err;
  }
  if (typeof value === 'object' && value !== null) {
    try {
      return new Error(JSON.stringify(value));
    } catch {
      return new Error('[non-serializable object]');
    }
  }
  return new Error(String(value));
}

function isHttpErrorResponse(err: Error): err is Error & { url: string; status: number } {
  return (
    err.name === 'HttpErrorResponse' &&
    typeof (err as any).url === 'string' &&
    typeof (err as any).status === 'number'
  );
}

export async function reportToHeimdall(error: unknown): Promise<void> {
  const isDevServer = (globalThis as any).devServer ?? false;
  if (isDevServer) {
    console.error(error);
    return;
  }

  let err = toError(error);
  while ((err as any)['ngOriginalError']) {
    err = toError((err as any)['ngOriginalError']);
  }
  while (err.cause instanceof Error) {
    err = err.cause;
  }

  // ChunkLoadError: webpack failed to fetch a lazy chunk (network blip, stale SW cache) — not an app bug.
  // "Script error.": browser hides the message/stack for cross-origin scripts without CORS headers — nothing useful to report.
  if (err.name === 'ChunkLoadError' || err.message === 'Script error.') {
    return;
  }

  let frames: StackFrame[] = [];
  try {
    const { parseStack } = await import('./stack-parser');
    frames = err.stack ? parseStack(err.stack) : [];
  } catch {
    // ignore dynamic import errors (e.g. ChunkLoadError)
  }
  const project = (globalThis as any)['appId'] ?? 'unknown';
  const environment = (globalThis as any)['environment'] ?? 'unknown';
  const deployment = (globalThis as any)['deployment'] ?? 'unknown';

  // status 0 = network-level failure (CORS, request cancelled, no connectivity) — not an app bug.
  if (isHttpErrorResponse(err) && (err.status === 0 || (err.status >= 400 && err.status < 500))) {
    return;
  }

  const mappedFrames = frames.map((f) => ({
    filename: f.filename,
    line_number: f.lineNumber,
    col_number: f.colNumber,
    function: f.function,
    // App bundles are served from cdn.apigateway.co — CDN frames ARE app code, not third-party.
    in_app: f.filename.includes('cdn.apigateway.co/'),
  }));

  if (isHttpErrorResponse(err)) {
    mappedFrames.unshift({
      filename: err.url,
      line_number: 0,
      col_number: 0,
      function: String(err.status),
      in_app: true,
    });
  }

  const payload = {
    report: {
      project,
      environment,
      deployment,
      url: window.location.href,
      message: err.message,
      exception: {
        type: err.name,
        value: err.message,
        frames: mappedFrames,
      },
    },
  };

  const endpoint =
    (globalThis as any).heimdallEndpoint ??
    'https://heimdall-api-prod.apigateway.co/heimdall.v2.Heimdall/CreateErrorReport';

  // Bare fetch instead of the Heimdall SDK — we only need one endpoint and the SDK would add
  // a significant bundle weight to every app that imports @vendasta/core.
  fetch(endpoint, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
}

@Injectable()
export class HeimdallErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error(error);
    reportToHeimdall(error);
  }
}
